/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Dec 15 2023
 **/

import { Theme, css } from "@emotion/react";
import styled from "@emotion/styled";

import { Button, Column, Grid, Row, Tab, Typography, TypographyCSS } from "@/frameworks/designStuff/components";
import { hoverScrollCss } from "@/frameworks/designStuff/utils";

export const IconArea = styled(Row)<{ variant?: ChatUI.Size }>`
	padding: ${({ variant }) => (variant === "transparent" ? "8px 16px 10px 16px" : "8px 16px 10px")};
	margin-bottom: ${({ variant }) => (variant === "transparent" ? 0 : "16px")};
	border-top: none;
	background: ${({ theme, variant }) =>
		variant === "transparent" ? theme.colors.neutral_00_80 : theme.colors.neutral_00_100};
	width: -webkit-fill-available;
	border-radius: 0 0 12px 12px;
`;

export const QuillCSS = ({ theme, isShowToolbar }: { theme: Theme; isShowToolbar: boolean }) => css`
	background-color: ${theme.colors.neutral_00_100};
	border-radius: 12px 12px 0 0;
	position: relative;
	z-index: 1;
	width: -webkit-fill-available;
	.ql-toolbar {
		border-radius: 12px 12px 0 0;
		border-bottom: none;
		display: ${!isShowToolbar && "none"};
	}
	.ql-container {
		max-height: 80px;
		overflow: auto;
		border-bottom: none;
	}
	.mention {
		${TypographyCSS("Title2", "accent_40_100")}
	}
	.ql-mention-list {
		padding: 8px;
		border-radius: 8px;
		background-color: #fff;
		box-shadow:
			0px 8px 16px 0px rgba(0, 0, 0, 0.2),
			0px 0px 4px 0px rgba(0, 0, 0, 0.04);
	}
	.mention-list-item {
	}
	.ql-editor {
		${TypographyCSS("Body2")}
	}
`;

// EmoticonPicker.tsx

export const Wrapper = styled(Column)`
	width: 340px;
	height: 288px;
	background: ${({ theme }) => theme.colors.neutral_00_100};
	padding: 12px 2px 0px 20px;
	box-shadow:
		0px 8px 16px 0px rgba(0, 0, 0, 0.2),
		0px 0px 4px 0px rgba(0, 0, 0, 0.04);
	border-radius: 8px;
`;

export const RepresentativeWrapper = styled(Row)`
	padding-bottom: 8px;
	padding-right: 20px;
	align-items: center;
	width: 100%;
`;

export const RepresentativeEmoji = styled.img`
	width: 28px;
	height: 28px;
	cursor: pointer;
`;

export const EmojiItem = styled.img`
	width: 60px;
	height: 60px;
	cursor: pointer;
`;

export const Info = styled(Row)`
	padding: 6px 8px;
	margin-right: 20px;
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.warning_00_100};
	justify-content: center;
`;

export const InfoEmoji = styled(Row)`
	padding: 4px;
	margin-right: 20px;
	border-top: 1px solid ${({ theme }) => theme.colors.neutral_50_100};
	justify-content: center;
`;

export const EmojiTitle = styled(Row)<{ isClicked: boolean }>`
	width: 28px;
	height: 28px;
	border-radius: 8px;
	padding: 4px;
	background-color: ${({ theme, isClicked }) => (isClicked ? theme.colors.secondary_80_15 : "transparent")};
	cursor: pointer;
`;

export const Emoji = styled.button`
	border-radius: 6px;
	padding: 4px;
	font-size: 29px;
	cursor: pointer;
	&:hover {
		background-color: ${({ theme }) => theme.colors.secondary_80_8};
	}
`;

export const IconLabel = styled.label`
	padding: 4px;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	transition: all 0.25s ease-in-out;
	cursor: pointer;
	&:hover {
		background-color: ${({ theme }) => theme.colors.secondary_80_8};
	}
`;

export const FileWrapper = styled(Column.ul)`
	width: 100%;
	padding: 8px 12px;
	background-color: ${({ theme }) => theme.colors.deco8_10_100};
	max-height: 150px;
	overflow: auto;
	border-radius: 0 !important;
	${hoverScrollCss()};
`;

export const EmoticonWrapper = styled(Row.ul)`
	width: 100%;
	padding: 8px 12px;
	background-color: ${({ theme }) => theme.colors.deco8_10_100};
	max-height: 150px;
	overflow: auto;
	flex-wrap: wrap;
	${hoverScrollCss()};
`;

export const FileItem = styled(Row)`
	position: relative;
	width: 100%;
	max-width: 280px;
	padding: 6px 10px;
	border-radius: 4px;
	border: 1px solid ${({ theme }) => theme.colors.chat_border};
	background-color: ${({ theme }) => theme.colors.neutral_00_68};
	height: 50px;
	&:hover {
		background-color: ${({ theme }) => theme.colors.neutral_80_50};
		#remove-file {
			display: flex !important;
		}
		cursor: pointer;
	}
`;

export const ImageItem = styled(Row)<{ only?: boolean }>`
	position: relative;
	max-width: 100%;
	height: 80px;
	${({ only = true }) => !only && "width: 86px"};
	border-radius: 4px;
	background-color: ${({ theme }) => theme.colors.neutral_80_50};
	overflow: hidden;
	&:hover {
		#remove-file {
			display: flex !important;
		}
		cursor: pointer;
	}
	img {
		height: 100%;
		width: auto;
	}
`;

export const RemoveFile = styled(Button.Icon)<{ position?: number }>`
	position: absolute !important;
	top: ${({ position = 8 }) => position}px;
	right: ${({ position = 8 }) => position}px;
	display: none !important;
`;

// SystemChat.tsx

export const SystemChatRow = styled(Row)`
	width: 100%;
`;

export const IconWrapper = css`
	width: 28px;
	height: 28px;
	border-radius: 50%;
	justify-content: center;
	align-items: center;
`;

export const IconCallWrapper = styled(Row)`
	${IconWrapper}
	background-color: ${({ theme }) => theme.colors.accent_50_100};
`;
export const IconMissedCallWrapper = styled(Row)`
	${IconWrapper}
	background-color: ${({ theme }) => theme.colors.warning_00_100};
`;

export const ChatContent = styled(Row)<{
	variant: ChatUI.Size;
}>`
	justify-content: space-between;
	width: 100%;
	align-items: center;
	gap: 8px;
`;

export const StyledChatInput = styled.div<{ update?: boolean; forceDisable?: boolean; variant?: ChatUI.Size }>`
	width: 100%;
	position: relative;
	border-radius: ${({ update, variant }) => (variant === "fullSize" || update) && "8px"};
	border: ${({ theme, variant }) => variant === "fullSize" && `1px solid ${theme.colors.neutral_30_100}`};
	opacity: ${({ forceDisable }) => forceDisable && "0.5"};
	border-radius: 8px;
	background: ${({ theme, variant }) =>
		variant === "transparent" ? "transparent" : `${theme.colors.neutral_00_100}`};
`;

export const editorCSS = (maxLine: number) => css`
	max-height: calc(20px * ${maxLine} + 16px);
`;

export const mentionListCSS = css`
	${hoverScrollCss()}
	padding: 8px;
	width: 270px;
	max-height: calc(32px * 4 + 8px * 2);
	border-radius: 8px;
	box-shadow:
		0px 8px 16px 0px rgba(0, 0, 0, 0.2),
		0px 0px 4px 0px rgba(0, 0, 0, 0.04);
`;

export const mentionItemCSS = (theme: Theme) => css`
	height: 32px;
	width: 100%;
	display: flex;
	align-items: center;
	border-radius: 8px;
	${TypographyCSS("Body3_MD", "neutral_80_100")};
	padding-left: 5px;
	padding-right: 6px;
	&.selected {
		background-color: ${theme.colors.secondary_80_16};
	}
	cursor: pointer;
`;

export const sirenCSS = (theme: Theme) => css`
	background-color: ${theme.colors.warning_00_100};
`;

export const sirenEditorCSS = (theme: Theme) => css`
	color: ${theme.colors.warning_80_100};
	max-height: calc(22px * 8);
`;

export const formatsCSS = (theme: Theme) => css`
	display: flex;
	gap: 1px;

	button {
		width: 24px;
		border-radius: 50%;
		padding: 4px;
		&:hover {
			background-color: ${theme.colors.secondary_80_8};
		}
	}
`;

export const toolbarCSS = (variant?: ChatUI.Size, update?: boolean) => css`
	${(() => {
		if (variant === "transparent" && update) {
			return "padding: 8px 3px;";
		}
	})()}
`;

export const radiusEditor = (theme: Theme, variant: ChatUI.Size, update?: boolean) => css`
	${(() => {
		if (update) return "border-radius: 8px;";
		switch (variant) {
			case "fullSize": {
				return "border-radius: 12px 12px 0 0;";
			}
			case "transparent": {
				return `
					border-radius: 8px 8px 0 0;
					background: ${theme.colors.neutral_00_80};
				`;
			}
			case "miniPopup": {
				return `border-top: 1px solid ${theme.colors.secondary_80_16}`;
			}
		}
	})()}
`;

export const WrapContentEmotion = styled(Column)`
	overflow: auto;
	height: 185px;
	padding: 16px 0;
`;

export const DisplayEmotion = styled.div`
	overflow: auto;
	height: 222px;
	width: 100%;
`;

export const EmotionList = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
`;

export const WrapperTab = styled(Tab)`
	height: 42px;
	padding: 0px 2px !important;
	width: calc(264px);
`;

export const WrapGrid = styled(Grid)`
	height: 160px;
	width: 100%;
`;
export const Date = styled(Typography)`
	color: ${({ theme }) => theme.colors.neutral_60_100};
`;

export const Sender = styled(Typography.p)`
	max-width: 130px;
	overflow: hidden;
	text-overflow: ellipsis;
	word-break: break-all;
	white-space: nowrap;
`;

export const CallButton = styled.button`
	width: 48px;
	height: 24px;
	display: flex;
	border-radius: 20px;
	border: none;
	background-color: ${({ theme }) => theme.colors.success_60_100};
	cursor: pointer;
	justify-content: center;
	align-items: center;
	margin-left: auto;
`;

//ChatVote.tsx
export const ChatVoteWrapper = styled(Column)`
	position: relative;
	width: 600px;
	height: 691px;
	border-radius: 12px;
	background-color: #fff;
`;

export const ContentWrapper = styled(Column)`
	height: calc(100% - 57px);
	width: 100%;
	padding: 28px 32px;
`;
export const VoteTitle = styled(Column)`
	width: 100%;
`;

export const VoteItem = styled(VoteTitle)``;

export const VoteOption = styled(VoteTitle)``;

export const VoteItemWrapper = styled(Row)`
	width: 100%;
	flex-wrap: wrap;
`;

export const LabelWrapper = styled(Row)`
	width: calc(100% / 2);
`;

export const BottomBtn = styled(Row)`
	border-top: 1px solid ${({ theme }) => theme.colors.neutral_50_40};
	padding: 10px;
	width: 100%;
	height: 57px;
	position: absolute;
	bottom: 0;
	background-color: #fff;
	border-radius: 0 0 12px 12px;
`;

export const FooterButton = styled(Button)`
	min-width: 120px;
`;

export const VoteWrapper = styled(Column)`
	width: 100%;
	max-width: 520px;
`;

export const TitleWrapper = styled(Column)`
	margin: 0 0 4px 0;
`;

export const VoteHeader = styled(Row)`
	width: 100%;
	position: relative;
	padding: 0 0 6px 0;
	margin: 0 0 4px 0;

	&::after {
		content: "";
		position: absolute;
		left: 0;
		bottom: 0;
		display: block;
		border-bottom: 1px dotted ${({ theme }) => theme.colors.neutral_50_20};
		width: 100%;
		height: 2px;
	}
`;

export const VoteContent = styled(Column)`
	width: 100%;
	border-radius: 8px;
	border: 1px solid ${({ theme }) => theme.colors.neutral_30_100};
	background-color: ${({ theme }) => theme.colors.neutral_00_100};
`;

export const VoteBox = styled(Column)`
	padding: 8px 10px 6px;
	width: 100%;
`;

export const VoteListBox = styled(Column.ul)`
	width: 100%;
	gap: 4px;
`;

export const VoteRenderItem = styled.li`
	padding: 8px 12px;
	width: 100%;
	border-radius: 8px;
	background: ${({ theme }) => theme.colors.neutral_50_8};
	cursor: pointer;
	transition: background 0.25s ease-in-out;

	&:hover,
	&.selected {
		background: ${({ theme }) => theme.colors.accent_10_100};
	}
`;

export const VoteMoreWrapper = styled(Column)`
	width: fit-content;
	padding: 8px;
	background: ${({ theme }) => theme.colors.neutral_00_100};
	border-radius: 8px;
	box-shadow:
		0px 0px 4px 0px rgba(0, 0, 0, 0.04),
		0px 8px 16px 0px rgba(0, 0, 0, 0.2);
`;

export const VoteResultContent = styled(Column.ul)`
	gap: 4px;
	padding: 8px 10px;
	width: 100%;
	background: ${({ theme }) => theme.colors.neutral_50_8};
`;

export const VoteResultItem = styled(Column.li)`
	width: 100%;
	padding: 7px 12px;
	background: ${({ theme }) => theme.colors.neutral_00_100};
	border: 1px solid ${({ theme }) => theme.colors.neutral_10_100};
	border-radius: 8px;
`;

export const CountWrapper = styled(Row)`
	padding: 4px 8px;
	width: 100%;
`;

export const GraphArea = styled(Row)`
	width: 100%;
`;

export const GraphBarWrapper = styled(Row)`
	width: 200px;
	height: 6px;
	background: ${({ theme }) => theme.colors.neutral_10_100};
	border-radius: 3px;
`;

export const GraphBar = styled.div<{ count: number; totalCount: number; isHighest: boolean }>`
	width: ${({ count, totalCount }) => `${count > 0 ? (count / totalCount) * 100 : 0}%`};
	height: 6px;
	background: ${({ theme, isHighest }) => (isHighest ? theme.colors.accent_40_100 : theme.colors.accent_20_100)};
	border-radius: 3px;
	transition: all 0.25s ease-in;
`;

export const DeadlineWrapper = styled(Row)`
	padding: 8px 4px;
	width: 100%;
`;

export const TransparentVoteContent = styled(Row)`
	padding: 6px 10px;
	width: 100%;
	background: ${({ theme }) => theme.colors.neutral_00_68};
	border: 1px solid ${({ theme }) => theme.colors.neutral_30_100};
	border-radius: 4px;
	cursor: pointer;
`;

export const IconBox = styled(Row)`
	padding: 6px;
	background: ${({ theme }) => theme.colors.accent_40_100};
	border-radius: 8px;
`;

export const TransparentVoteBox = styled(Column)`
	width: 119px;

	p {
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 1;
		-webkit-box-orient: vertical;
	}
`;

export const VoterTitle = styled(Typography.p)`
	width: 100%;
	max-height: 32px;
	overflow: hidden;
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2;
	word-break: break-word;
`;
